import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const DEFAULT_DATA = {
  hero: {
    title: "Contact WarmWellness",
    subtitle: "We're here to answer your questions and help with your healthcare needs",
    background: "linear-gradient(to right, #0760ad, #098a48)"
  },
  contactInfo: {
    phones: [
      "+880 1234 567890",
      "+880 9876 543210 (Emergency)"
    ],
    emails: [
      "info@warmwellness.com",
      "appointments@warmwellness.com"
    ],
    address: "123 Healthcare Avenue, Dhaka 1212",
    openingHours: [
      {
        days: "Monday - Friday",
        hours: "8:00 AM - 8:00 PM"
      },
      {
        days: "Saturday",
        hours: "9:00 AM - 5:00 PM"
      },
      {
        days: "Sunday",
        hours: "Emergency Only"
      }
    ]
  },
  mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.793481392088!2d90.4062723154319!3d23.750859984587886!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755b8b33cffc3fb%3A0x4a826f475fd312af!2sDhaka%2C%20Bangladesh!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus",
  cta: {
    title: "Ready to Visit Us?",
    buttons: [
      {
        text: "Book Appointment",
        link: "/appointment",
        style: "primary"
      },
      {
        text: "View All Locations",
        link: "/locations",
        style: "secondary"
      }
    ]
  }
};

const COLORS = {
  primary: "#0760ad",
  primaryLight: "#098a48",
  secondary: "#118a94",
  white: "#FFFFFF",
  lightBg: "#f8fafc"
};

const ContactSection = () => {
  const [contactData, setContactData] = useState(DEFAULT_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/contact/`);
        if (response.ok) {
          const data = await response.json();
          setContactData(data);
        }
      } catch (error) {
        console.error('Error fetching contact data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Here you would typically send the form data to your backend
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <div 
        className="py-20 px-4 sm:px-6 lg:px-8 text-center"
        style={{ background: contactData.hero?.background || DEFAULT_DATA.hero.background }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          {contactData.hero?.title || DEFAULT_DATA.hero.title}
        </h1>
        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
          {contactData.hero?.subtitle || DEFAULT_DATA.hero.subtitle}
        </p>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl font-bold mb-8" style={{ color: COLORS.primary }}>Our Information</h2>
            
            <div className="space-y-6 mb-10">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1 text-[#118a94]">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Phone</h3>
                  {(contactData.contactInfo?.phones || DEFAULT_DATA.contactInfo.phones).map((phone, index) => (
                    <p key={index} className="text-gray-700">
                      {phone}
                    </p>
                  ))}
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1 text-[#118a94]">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Email</h3>
                  {(contactData.contactInfo?.emails || DEFAULT_DATA.contactInfo.emails).map((email, index) => (
                    <p key={index} className="text-gray-700">
                      {email}
                    </p>
                  ))}
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1 text-[#118a94]">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium">Main Center</h3>
                  <p className="text-gray-700">
                    {contactData.contactInfo?.address || DEFAULT_DATA.contactInfo.address}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Opening Hours</h3>
              <ul className="space-y-3">
                {(contactData.contactInfo?.openingHours || DEFAULT_DATA.contactInfo.openingHours).map((hours, index) => (
                  <li key={index} className="flex justify-between">
                    <span className="text-gray-700">{hours.days}</span>
                    <span className="font-medium">{hours.hours}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
            <h2 className="text-2xl font-bold mb-6" style={{ color: COLORS.primary }}>
              Send Us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="appointment">Book an Appointment</option>
                  <option value="general">General Inquiry</option>
                  <option value="feedback">Feedback</option>
                  <option value="emergency">Emergency Contact</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0760ad] focus:border-[#0760ad] transition"
                  required
                ></textarea>
              </div>

              <div>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 px-6 rounded-md font-medium text-white shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  Send Message
                </motion.button>
              </div>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="aspect-w-16 aspect-h-9">
            <iframe 
              src={contactData.mapEmbedUrl || DEFAULT_DATA.mapEmbedUrl}
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="WarmWellness Location"
            ></iframe>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div 
        className="py-16 px-4 sm:px-6 lg:px-8 text-center"
        style={{ backgroundColor: COLORS.primary }}
      >
        <h2 className="text-3xl font-bold text-white mb-6">
          {contactData.cta?.title || DEFAULT_DATA.cta.title}
        </h2>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          {(contactData.cta?.buttons || DEFAULT_DATA.cta.buttons).map((button, index) => (
            <motion.a
              key={index}
              href={button.link}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-8 py-3 rounded-full font-medium shadow-lg ${
                button.style === 'primary' 
                  ? 'text-[#0760ad] bg-white' 
                  : 'text-white border-2 border-white'
              }`}
            >
              {button.text}
            </motion.a>
          ))}
        </div>
      </div>
    </>
  );
};

export default ContactSection;